import { type Dispatch, type FC, type SetStateAction } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Bleed, Box, Divider, Expandable, Popover, Ribbon, Stack, Text, TextLink } from '@eneco/sparky';
import { ElectricityIcon, GasIcon, GiftIcon, InfoIcon, SunIcon } from '@eneco/sparky/icons';

import type { FlowConsumerContextEnergy } from '../../../../context';
import { getPriceDetailsExplanationTexts } from '../../../../shared/helpers';
import type { Offer } from '../../../../shared/types';
import type {
  OxxioElectricityRedeliverySection,
  OxxioElectricityVariableSection,
  OxxioFixedCostsSection,
  OxxioGasVariableSection,
  OxxioGovernmentLeviesSection,
  OxxioRedeliveryBlock,
  OxxioSectionHeader,
  OxxioTariffRow,
  OxxioTotalsSection,
} from '../builders/oxxio-types';
import { LabelAndTariffAndUsageDetails, TariffAndUsageDetails } from '../price-details-overview-oxxio.helpers';
import type { FieldsUnionOxxio } from '../price-details-overview-oxxio.types';

const OxxioSectionHeaderRow: FC<{ header: OxxioSectionHeader }> = ({ header }) => {
  const { currency } = useFormatter();

  return (
    <Bleed horizontal="3" vertical="3">
      <Box backgroundColor="backgroundPrimary" padding="3">
        <Text weight="bold">
          <Stack direction="row" alignY="start" gap="2">
            <Stack.Item grow={true}>{header.label}</Stack.Item>
            <Stack.Item shrink={false}>{currency.euro(header.value)}</Stack.Item>
          </Stack>
        </Text>
      </Box>
    </Bleed>
  );
};

const OxxioHighDivider: FC = () => (
  <Bleed horizontal="3">
    <Divider emphasis="high" />
  </Bleed>
);

const OxxioDivider: FC = () => (
  <Bleed horizontal="3">
    <Divider />
  </Bleed>
);

const OxxioTariffIcon: FC<{ icon: OxxioTariffRow['icon'] }> = ({ icon }) => {
  if (icon === 'gas') {
    return <GasIcon color="iconGas" />;
  }

  if (icon === 'sun') {
    return <SunIcon color="iconSolar" />;
  }

  return <ElectricityIcon color="iconElectricity" />;
};

const OxxioStandardTariffRow: FC<{ row: OxxioTariffRow }> = ({ row }) => {
  const { currency, unit } = useFormatter();
  const formatUsage = row.icon === 'gas' ? unit.gas : unit.electricity;

  return (
    <Stack.Item>
      <Stack direction="row" alignY="start" gap="2">
        {row.icon && (
          <Stack.Item shrink={false}>
            <OxxioTariffIcon icon={row.icon} />
          </Stack.Item>
        )}
        <Stack.Item grow={true}>
          <Stack direction="column" gap="2">
            <Text weight="bold">{row.label}</Text>
            <Stack direction="row" alignY="end" gap="2">
              <Stack.Item grow={true}>
                {`${currency.euroFiveDecimals(row.tariff)} × ${formatUsage(row.usage)}${row.icon === 'gas' ? '' : ' '} = `}
              </Stack.Item>
              <Stack.Item shrink={false}>{currency.euro(row.total)}</Stack.Item>
            </Stack>
          </Stack>
        </Stack.Item>
      </Stack>
    </Stack.Item>
  );
};

const OxxioPurchaseFeeRows: FC<{ section: NonNullable<OxxioElectricityVariableSection['purchaseFee']> }> = ({
  section,
}) => (
  <>
    <LabelAndTariffAndUsageDetails
      label={section.marketPrice.label}
      tariff={section.marketPrice.tariff}
      usage={section.marketPrice.usage}
    />
    <Stack.Item>
      <Stack direction="row" alignY="start" gap="2">
        <Stack.Item grow={true}>
          <Stack direction="column" gap="2">
            <TariffAndUsageDetails
              tariff={section.marketPrice.tariff}
              usage={section.marketPrice.usage}
              total={section.marketPrice.total}
            />
          </Stack>
        </Stack.Item>
      </Stack>
    </Stack.Item>
    <LabelAndTariffAndUsageDetails
      label={section.purchaseFee.label}
      tariff={section.purchaseFee.tariff}
      usage={section.purchaseFee.usage}
    />
    <Stack.Item>
      <Stack direction="row" alignY="start" gap="2">
        <Stack.Item grow={true}>
          <Stack direction="column" gap="2">
            <TariffAndUsageDetails
              tariff={section.purchaseFee.tariff}
              usage={section.purchaseFee.usage}
              total={section.purchaseFee.total}
            />
          </Stack>
        </Stack.Item>
      </Stack>
    </Stack.Item>
  </>
);

export const OxxioElectricityVariableSectionView: FC<{ section: OxxioElectricityVariableSection }> = ({ section }) => (
  <>
    <OxxioSectionHeaderRow header={section.header} />
    <OxxioHighDivider />
    {section.purchaseFee ? <OxxioPurchaseFeeRows section={section.purchaseFee} /> : null}
    {section.standardTariff ? (
      <>
        <OxxioStandardTariffRow row={section.standardTariff.high} />
        {section.standardTariff.low ? (
          <>
            <OxxioDivider />
            <OxxioStandardTariffRow row={section.standardTariff.low} />
          </>
        ) : null}
      </>
    ) : null}
  </>
);

const OxxioRedeliveryBlockView: FC<{ block: OxxioRedeliveryBlock }> = ({ block }) => {
  const { currency } = useFormatter();

  if (block.type === 'header') {
    return (
      <>
        <OxxioHighDivider />
        <Bleed horizontal="3" vertical="3">
          <Box backgroundColor="backgroundPrimary" padding="3">
            <Text weight="bold">
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" gap="2">
                    <Text>Teruglevering</Text>
                    {block.showPopover && block.popoverTitle ? (
                      <Popover
                        size="large"
                        side="bottom"
                        title={block.popoverTitle}
                        trigger={
                          <TextLink
                            ariaLabel="Meer informatie over teruglevering"
                            leftIcon={<InfoIcon size="small" verticalAlign="text-top" color="currentColor" />}>
                            {''}
                          </TextLink>
                        }>
                        <RichText html={block.popoverContent} />
                      </Popover>
                    ) : null}
                  </Stack>
                </Stack.Item>
                <Stack.Item shrink={false}>{currency.euro(block.total)}</Stack.Item>
              </Stack>
            </Text>
          </Box>
        </Bleed>
      </>
    );
  }

  if (block.type === 'yearTitle') {
    return (
      <>
        <OxxioDivider />
        <Stack.Item>
          <Stack direction="row" alignY="start" gap="2">
            <Stack.Item shrink={false}>
              <SunIcon color="iconSolar" />
            </Stack.Item>
            <Stack.Item grow={true}>
              <Stack direction="column" gap="2">
                <Text weight="bold">{`Teruglevering ${block.year}`}</Text>
              </Stack>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </>
    );
  }

  if (block.type === 'tariff' || block.type === 'salesFee') {
    return (
      <>
        {block.type === 'tariff' && block.showDividerBefore ? <OxxioDivider /> : null}
        <LabelAndTariffAndUsageDetails label={block.label} tariff={block.tariff} usage={block.usage} />
        <Stack.Item>
          <Stack direction="row" alignY="start" gap="2">
            <Stack.Item grow={true}>
              <Stack direction="column" gap="2">
                <TariffAndUsageDetails tariff={block.tariff} usage={block.usage} total={block.total} />
              </Stack>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </>
    );
  }

  return null;
};

export const OxxioElectricityRedeliverySectionView: FC<{ section: OxxioElectricityRedeliverySection }> = ({
  section,
}) => (
  <>
    {section.blocks.map(block => {
      const getKey = () => {
        if (block.type === 'yearTitle') return `yearTitle-${block.year}`;
        if (block.type === 'header') return 'header';

        return `${block.type}-${block.label}`;
      };

      return <OxxioRedeliveryBlockView key={getKey()} block={block} />;
    })}
  </>
);

export const OxxioGasVariableSectionView: FC<{ section: OxxioGasVariableSection }> = ({ section }) => (
  <>
    <OxxioDivider />
    <OxxioSectionHeaderRow header={section.header} />
    <OxxioHighDivider />
    {section.purchaseFee ? <OxxioPurchaseFeeRows section={section.purchaseFee} /> : null}
    {section.standardTariff ? <OxxioStandardTariffRow row={section.standardTariff} /> : null}
  </>
);

export const OxxioGovernmentLeviesSectionView: FC<{ section: OxxioGovernmentLeviesSection }> = ({ section }) => {
  const { currency, unit } = useFormatter();
  const formatUsage = section.usageUnit === 'gas' ? unit.gas : unit.electricity;

  return (
    <>
      <OxxioHighDivider />
      <OxxioSectionHeaderRow header={section.header} />
      <OxxioHighDivider />
      <Stack.Item>
        <Stack direction="row" alignY="start" gap="2">
          <Stack.Item shrink={false}>
            <OxxioTariffIcon icon={section.icon} />
          </Stack.Item>
          <Stack.Item grow={true}>
            <Stack direction="column" gap="2">
              <Text weight="bold">{section.productLabel}</Text>
              {section.brackets.map(bracket => (
                <Text color="textLowEmphasis" key={bracket.usageRange}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>{bracket.usageRange}</Stack.Item>
                    <Stack.Item shrink={false}>
                      {`${currency.euroHighPrecision(bracket.price)} ${bracket.unit}`}
                    </Stack.Item>
                  </Stack>
                </Text>
              ))}
              <Stack.Item>
                <Stack direction="row" alignY="start" gap="2">
                  <Stack.Item grow={true}>{`${section.usageLabel}: ${formatUsage(section.usageTotal)}`}</Stack.Item>
                  <Stack.Item shrink={false}>{currency.euroHighPrecision(section.totalTax)}</Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Stack.Item>
    </>
  );
};

export const OxxioFixedCostsSectionView: FC<{
  section: OxxioFixedCostsSection;
  showLeadingDivider?: boolean;
  showTrailingDivider?: boolean;
}> = ({ section, showLeadingDivider = true, showTrailingDivider = true }) => {
  const { currency } = useFormatter();

  return (
    <>
      {showLeadingDivider ? <OxxioHighDivider /> : null}
      <OxxioSectionHeaderRow header={section.header} />
      <OxxioHighDivider />
      <Stack direction="column" gap="4">
        {section.lines.map(line => (
          <Stack.Item key={line.label}>
            <Stack direction="row" gap="3">
              <Stack.Item grow={true}>{line.label}</Stack.Item>
              <Stack.Item shrink={false}>{currency.euro(line.value)}</Stack.Item>
            </Stack>
          </Stack.Item>
        ))}
      </Stack>
      {showTrailingDivider ? <OxxioHighDivider /> : null}
    </>
  );
};

export const OxxioSubtotalSectionView: FC<{ label?: string; value: number }> = ({ label, value }) => {
  const { currency } = useFormatter();

  return (
    <Bleed horizontal="3" vertical="3">
      <Box backgroundColor="backgroundPrimary" padding="3">
        <Text weight="bold">
          <Stack direction="row" gap="2">
            <Stack.Item grow={true}>{label}</Stack.Item>
            <Stack.Item shrink={false}>{currency.euro(value)}</Stack.Item>
          </Stack>
        </Text>
      </Box>
    </Bleed>
  );
};

export const OxxioTotalsSectionView: FC<{ section: OxxioTotalsSection }> = ({ section }) => {
  const { currency } = useFormatter();

  return (
    <>
      <OxxioHighDivider />
      <Stack direction="column" gap="4">
        {section.cashbackTexts ? (
          <Bleed vertical="3" horizontal="3">
            <Ribbon emphasis="medium">
              <Box paddingX="3">
                <Text size="BodyS">
                  <Stack direction="row" gap="3">
                    <Stack.Item grow={true}>
                      <Text align="left">
                        <GiftIcon size="small" color="currentColor" verticalAlign="text-top" />{' '}
                        <RichText html={section.cashbackTexts.label ?? ''} />{' '}
                        <Popover
                          size="small"
                          side="bottom"
                          title={section.cashbackTexts.title ?? ''}
                          trigger={
                            <TextLink leftIcon={<InfoIcon size="small" color="currentColor" verticalAlign="bottom" />}>
                              {section.cashbackTexts.trigger}
                            </TextLink>
                          }>
                          <RichText html={section.cashbackTexts.text} />
                        </Popover>
                      </Text>
                    </Stack.Item>
                    <Stack.Item shrink={false}>- {currency.euro(section.cashbackValue ?? 0)}</Stack.Item>
                  </Stack>
                </Text>
              </Box>
            </Ribbon>
          </Bleed>
        ) : null}

        <Stack.Item>
          <Stack direction="row" gap="3">
            <Stack.Item grow={true}>{section.yearlyLabel}</Stack.Item>
            <Stack.Item shrink={false}>{currency.euro(section.yearlyTotal)}</Stack.Item>
          </Stack>
        </Stack.Item>

        <Stack.Item>
          <Stack direction="row" gap="3">
            <Stack.Item grow={true}>{section.monthlyLabel}</Stack.Item>
            <Stack.Item shrink={false}>{currency.euro(section.monthlyTotal)}</Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </>
  );
};

interface OxxioExplanationSectionViewProps {
  flowContext: FlowConsumerContextEnergy;
  offer?: Offer;
  textualData?: FieldsUnionOxxio;
  isOpen?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  hasFutureRate: boolean;
  productDetailsTariffSplitExplanation?: string;
}

export const OxxioExplanationSectionView: FC<OxxioExplanationSectionViewProps> = ({
  flowContext,
  offer,
  textualData,
  isOpen,
  setOpen,
  hasFutureRate,
  productDetailsTariffSplitExplanation,
}) => (
  <>
    <Bleed horizontal="3" bottom="3">
      <Divider emphasis="high" />
    </Bleed>
    {/* @ts-ignore -- TODO: fix isOpen & setOpen optional props */}
    <Expandable isOpen={isOpen} setOpen={setOpen}>
      <Bleed horizontal="3">
        <Box paddingX="3" backgroundColor="backgroundPrimary">
          <Expandable.Trigger isTransparent aria-labelledby="price-details">
            <Box paddingY="3">{textualData?.costDetailsExplanation?.explanationTitle?.value}</Box>
          </Expandable.Trigger>
        </Box>
      </Bleed>

      <Bleed horizontal="3">
        <Expandable.Content>
          <Divider emphasis="high" />
          <Box paddingTop="3" paddingX="3">
            {hasFutureRate ? (
              <Box paddingTop={{ initial: '3', md: '0' }} paddingBottom={3}>
                <Text size="BodyS">
                  <RichText html={productDetailsTariffSplitExplanation} />
                </Text>
              </Box>
            ) : null}
            <RichText html={getPriceDetailsExplanationTexts(flowContext, offer, textualData?.costDetailsExplanation)} />
          </Box>
        </Expandable.Content>
      </Bleed>
    </Expandable>
  </>
);
